import { forwardRef } from 'react';
import { cn } from '../../utils/cn';
import { Icon } from './Icon';
import { LucideIcon } from 'lucide-react';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  helper?: string;
  leftIcon?: LucideIcon;
  rightIcon?: LucideIcon;
  size?: 'sm' | 'md' | 'lg';
  onRightIconClick?: () => void;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      className,
      label,
      error,
      helper,
      leftIcon,
      rightIcon,
      size = 'md',
      type = 'text',
      required,
      onRightIconClick,
      ...props
    },
    ref
  ) => {
    const sizes = {
      sm: 'input-sm',
      md: 'input-md',
      lg: 'input-lg',
    };

    return (
      <div className="form-group">
        {label && (
          <label className={cn('form-label', required && 'form-label-required')}>
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
              <Icon icon={leftIcon} size={size === 'lg' ? 'md' : 'sm'} />
            </div>
          )}
          <input
            ref={ref}
            type={type}
            className={cn(
              'input',
              sizes[size],
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              error && 'input-error',
              className
            )}
            {...props}
          />
          {rightIcon && (
            <button
              type="button"
              onClick={onRightIconClick}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-primary-500 transition-colors cursor-pointer"
            >
              <Icon icon={rightIcon} size={size === 'lg' ? 'md' : 'sm'} />
            </button>
          )}
        </div>
        {helper && !error && <p className="form-helper">{helper}</p>}
        {error && <p className="form-error">{error}</p>}
      </div>
    );
  }
);

Input.displayName = 'Input';
